import React, { useCallback, useEffect, useState } from "react";
import {
  createDraft,
  getDraft,
  listDrafts,
  makeDraftPick,
  startDraft,
} from "../../api/communityApi";

const Badge = ({ status }) => {
  const map = {
    pending: { color: "bg-amber-500/15 text-amber-500", label: "Pending" },
    drafting: { color: "bg-purple-500/15 text-purple-500", label: "Drafting" },
    active: { color: "bg-emerald-500/15 text-emerald-500", label: "Live" },
    completed: { color: "bg-gray-500/15 text-gray-500", label: "Completed" },
  };
  const cfg = map[status] || map.pending;
  return (
    <span className={`text-[10px] font-bold px-2 py-0.5 rounded ${cfg.color}`}>
      {cfg.label.toUpperCase()}
    </span>
  );
};

const DraftCreator = ({ onCreated }) => {
  const [name, setName] = useState("");
  const [picks, setPicks] = useState(5);
  const [days, setDays] = useState(30);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim()) return;
    setBusy(true);
    setError(null);
    try {
      const draft = await createDraft({
        name: name.trim(),
        picks_per_user: picks,
        duration_days: days,
      });
      setName("");
      onCreated?.(draft);
    } catch {
      setError("Couldn't create draft. Try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} className="bg-gray-50 dark:bg-white/5 rounded-2xl p-5 border border-gray-200 dark:border-white/10 mb-6">
      <h3 className="font-bold mb-3">Create a new draft</h3>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <input
          type="text"
          placeholder="Draft name (e.g. Friends League Q4)"
          value={name}
          onChange={(e) => setName(e.target.value)}
          className="sm:col-span-3 w-full px-4 py-2.5 rounded-lg bg-white dark:bg-[#0b1220] border border-gray-300 dark:border-white/10"
          required
        />
        <div>
          <label className="text-xs text-gray-500">Picks per user</label>
          <input
            type="number"
            min={1}
            max={10}
            value={picks}
            onChange={(e) => setPicks(parseInt(e.target.value, 10) || 1)}
            className="w-full px-4 py-2.5 rounded-lg bg-white dark:bg-[#0b1220] border border-gray-300 dark:border-white/10 mt-1"
          />
        </div>
        <div>
          <label className="text-xs text-gray-500">Duration (days)</label>
          <input
            type="number"
            min={1}
            max={365}
            value={days}
            onChange={(e) => setDays(parseInt(e.target.value, 10) || 1)}
            className="w-full px-4 py-2.5 rounded-lg bg-white dark:bg-[#0b1220] border border-gray-300 dark:border-white/10 mt-1"
          />
        </div>
        <button
          type="submit"
          disabled={busy || !name.trim()}
          className="self-end px-4 py-2.5 rounded-lg bg-gradient-to-r from-emerald-500 to-sky-500 text-white font-semibold disabled:opacity-50"
        >
          {busy ? "Creating…" : "Create"}
        </button>
      </div>
      {error && <p className="text-red-500 text-sm mt-2">{error}</p>}
      <p className="text-xs text-gray-500 mt-3">
        After creating, you can invite friends and start the snake-draft. Picks lock for the duration window.
      </p>
    </form>
  );
};

const DraftDetail = ({ draft, currentUserId, onChange }) => {
  const [symbol, setSymbol] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  const isMyTurn = draft.status === "drafting" && draft.current_pick_user_id === currentUserId;
  const isCreator = draft.creator_id === currentUserId;
  const canStart = isCreator && draft.status === "pending" && draft.members.length >= 2;

  const handleStart = async () => {
    setBusy(true);
    setErr(null);
    try {
      const updated = await startDraft(draft.id);
      onChange?.(updated);
    } catch (e) {
      setErr(e.response?.data?.detail || "Couldn't start draft.");
    } finally {
      setBusy(false);
    }
  };

  const handlePick = async (e) => {
    e.preventDefault();
    if (!symbol.trim()) return;
    setBusy(true);
    setErr(null);
    try {
      const updated = await makeDraftPick(draft.id, symbol.trim().toUpperCase());
      setSymbol("");
      onChange?.(updated);
    } catch (e) {
      setErr(e.response?.data?.detail || "Couldn't pick that symbol.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="bg-white dark:bg-[#0b1220] rounded-2xl border border-gray-200 dark:border-white/10 p-5">
      <div className="flex items-start justify-between mb-4">
        <div>
          <div className="flex items-center gap-2">
            <h3 className="text-xl font-bold">{draft.name}</h3>
            <Badge status={draft.status} />
          </div>
          <p className="text-sm text-gray-500 mt-1">
            {draft.picks_per_user} picks · {draft.duration_days} days · {draft.members.length} member(s)
          </p>
        </div>
        {canStart && (
          <button
            onClick={handleStart}
            disabled={busy}
            className="px-4 py-2 rounded-lg bg-emerald-500 text-white font-semibold disabled:opacity-50"
          >
            Start Draft
          </button>
        )}
      </div>

      {err && <p className="text-red-500 text-sm mb-3">{err}</p>}

      {isMyTurn && (
        <form onSubmit={handlePick} className="bg-purple-500/10 border border-purple-500/30 rounded-xl p-4 mb-4">
          <p className="font-semibold text-purple-500 mb-2">
            Your pick — #{draft.current_pick_number} of {draft.total_picks}
          </p>
          <div className="flex gap-2">
            <input
              type="text"
              placeholder="Symbol (e.g. AAPL)"
              value={symbol}
              onChange={(e) => setSymbol(e.target.value.toUpperCase())}
              className="flex-1 px-4 py-2.5 rounded-lg bg-white dark:bg-[#060B12] border border-gray-300 dark:border-white/10"
            />
            <button
              type="submit"
              disabled={busy || !symbol.trim()}
              className="px-5 py-2.5 rounded-lg bg-purple-600 text-white font-semibold disabled:opacity-50"
            >
              Pick
            </button>
          </div>
        </form>
      )}

      {draft.status === "drafting" && !isMyTurn && (
        <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-3 mb-4 text-sm">
          Waiting on{" "}
          <strong>
            {draft.members.find((m) => m.user_id === draft.current_pick_user_id)?.display_name ?? "next player"}
          </strong>{" "}
          to pick #{draft.current_pick_number} of {draft.total_picks}.
        </div>
      )}

      <div className="space-y-2">
        {draft.members.map((m) => {
          const positive = m.performance_pct >= 0;
          return (
            <div
              key={m.user_id}
              className={`flex items-center gap-3 p-3 rounded-xl border ${
                m.is_you
                  ? "border-purple-500/40 bg-purple-500/5"
                  : "border-gray-200 dark:border-white/10"
              }`}
            >
              <img
                src={m.avatar_url || `https://ui-avatars.com/api/?name=${encodeURIComponent(m.display_name)}`}
                alt=""
                className="w-10 h-10 rounded-full"
              />
              <div className="flex-1 min-w-0">
                <p className="font-semibold flex items-center gap-2">
                  {m.display_name}
                  {m.is_creator && (
                    <span className="text-[9px] font-bold px-1.5 py-0.5 rounded bg-amber-500/15 text-amber-500">
                      HOST
                    </span>
                  )}
                  {m.is_you && (
                    <span className="text-[9px] font-bold px-1.5 py-0.5 rounded bg-purple-500/15 text-purple-500">
                      YOU
                    </span>
                  )}
                </p>
                <div className="flex flex-wrap gap-1 mt-1">
                  {m.picks.length === 0 && (
                    <span className="text-xs text-gray-500">No picks yet</span>
                  )}
                  {m.picks.map((p) => (
                    <span
                      key={p.symbol}
                      className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-gray-100 dark:bg-white/10"
                    >
                      {p.symbol}
                    </span>
                  ))}
                </div>
              </div>
              {draft.status === "active" || draft.status === "completed" ? (
                <span
                  className={`font-bold ${
                    positive ? "text-emerald-500" : "text-red-500"
                  }`}
                >
                  {positive ? "+" : ""}
                  {m.performance_pct?.toFixed(2)}%
                </span>
              ) : null}
            </div>
          );
        })}
      </div>
    </div>
  );
};

const DraftsTab = () => {
  const [drafts, setDrafts] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentUserId, setCurrentUserId] = useState(null);

  const refresh = useCallback(async () => {
    try {
      const data = await listDrafts();
      const list = Array.isArray(data?.drafts) ? data.drafts : [];
      setDrafts(list);
      if (list.length > 0 && selectedId == null) {
        setSelectedId(list[0].id);
      }
    } catch {
      setDrafts([]);
    } finally {
      setLoading(false);
    }
  }, [selectedId]);

  useEffect(() => {
    refresh();
    // grab current user from /api/auth/me/ via existing context if available
    const token = localStorage.getItem("access");
    if (token) {
      const payload = (() => {
        try {
          return JSON.parse(atob(token.split(".")[1]));
        } catch {
          return null;
        }
      })();
      if (payload?.user_id) setCurrentUserId(payload.user_id);
    }
  }, [refresh]);

  const selected = drafts.find((d) => d.id === selectedId);

  const updateOne = (updated) => {
    setDrafts((cur) => cur.map((d) => (d.id === updated.id ? updated : d)));
  };

  const onCreated = async (draft) => {
    setDrafts((cur) => [draft, ...cur]);
    setSelectedId(draft.id);
  };

  const refreshSelected = async () => {
    if (!selectedId) return;
    try {
      const fresh = await getDraft(selectedId);
      updateOne(fresh);
    } catch {
      // no-op
    }
  };

  return (
    <div>
      <DraftCreator onCreated={onCreated} />

      {loading ? (
        <p className="text-gray-500">Loading drafts…</p>
      ) : drafts.length === 0 ? (
        <div className="bg-gray-50 dark:bg-white/5 rounded-2xl p-8 border border-gray-200 dark:border-white/10 text-center">
          <p className="font-semibold mb-1">No drafts yet</p>
          <p className="text-sm text-gray-500">
            Create one above and invite friends. Snake-draft order, custom duration, automatic performance tracking.
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-[260px_1fr] gap-4">
          <div className="space-y-2">
            {drafts.map((d) => (
              <button
                key={d.id}
                onClick={() => setSelectedId(d.id)}
                className={`w-full text-left p-3 rounded-xl border transition ${
                  selectedId === d.id
                    ? "border-purple-500 bg-purple-500/5"
                    : "border-gray-200 dark:border-white/10 hover:border-gray-300 dark:hover:border-white/20"
                }`}
              >
                <div className="flex items-center gap-2 mb-1">
                  <p className="font-semibold truncate flex-1">{d.name}</p>
                  <Badge status={d.status} />
                </div>
                <p className="text-xs text-gray-500">
                  {d.members.length} member(s) · {d.picks_per_user} picks
                </p>
              </button>
            ))}
          </div>
          <div>
            {selected ? (
              <DraftDetail
                draft={selected}
                currentUserId={currentUserId}
                onChange={(updated) => {
                  updateOne(updated);
                  refreshSelected();
                }}
              />
            ) : (
              <p className="text-gray-500">Select a draft to view.</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default DraftsTab;
